import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Mail,
  MapPin,
  Phone,
  Users,
  CreditCard,
  Calendar,
  Building2,
  UserCircle,
  FileText,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Family } from '../../types/models';
import { getFamilyById, getFamilyPayments } from '../../services/familyService';
import { supabase } from '../../lib/supabase';

interface MemberDetail {
  student_id: string;
  first_name: string;
  last_name: string;
  first_name_tamil?: string | null;
  last_name_tamil?: string | null;
  date_of_birth: string;
  gender: string;
  reference_number?: string | null;
  payment_status?: string | null;
  department?: { name: string } | null;
  house?: { house_name: string; house_color: string } | null;
}

const calculateAge = (dob: string) => {
  const birth = parseISO(dob);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age;
};

const FamilyDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [family, setFamily] = useState<Family | null>(null);
  const [members, setMembers] = useState<MemberDetail[]>([]);
  const [payments, setPayments] = useState<
    Array<{ competition_year: number; status: string; amount?: number | null; paid_at?: string | null; method?: string | null; notes?: string | null }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const fam = await getFamilyById(id);
        if (!fam) {
          if (active) setError('Family not found');
          return;
        }
        const memberIds = (fam.members || []).map((m: any) => m.student_id);
        let enriched: MemberDetail[] = [];
        if (memberIds.length > 0) {
          const { data, error: memErr } = await supabase
            .from('students')
            .select(`
              student_id,
              first_name,
              last_name,
              first_name_tamil,
              last_name_tamil,
              date_of_birth,
              gender,
              reference_number,
              payment_status,
              department:departments(name),
              house:houses(house_name, house_color)
            `)
            .in('student_id', memberIds)
            .order('date_of_birth', { ascending: true });
          if (memErr) throw memErr;
          enriched = (data as any) || [];
        }
        const pay = await getFamilyPayments(id);
        if (!active) return;
        setFamily(fam);
        setMembers(enriched);
        setPayments(pay);
      } catch (err: any) {
        if (active) setError(err.message || 'Failed to load family');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse text-gray-500">Loading family...</div>
      </div>
    );
  }

  if (error || !family) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Link to="/participants/family-connections" className="text-blue-600 inline-flex items-center mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to families
        </Link>
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded">
          {error || 'Family not found'}
        </div>
      </div>
    );
  }

  const paymentColor: Record<string, string> = {
    paid: 'bg-green-100 text-green-800',
    school_paid: 'bg-blue-100 text-blue-800',
    not_paid: 'bg-red-100 text-red-800',
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <Link
          to="/participants/family-connections"
          className="text-blue-600 inline-flex items-center text-sm"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to families
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="h-6 w-6 text-blue-500 mr-2" />
              {family.family_name}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {family.department?.name && (
                <span className="inline-flex items-center mr-3">
                  <Building2 className="h-3.5 w-3.5 mr-1" />
                  {family.department.name}
                </span>
              )}
              <span>{members.length} member{members.length === 1 ? '' : 's'}</span>
            </p>
          </div>
          <button
            onClick={() => navigate(`/participants/family-connections?edit=${family.family_id}`)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit family
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-6 py-5">
          <section>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center">
              <UserCircle className="h-4 w-4 mr-1.5" />
              Parents / Guardians
            </h2>
            <dl className="space-y-2 text-sm">
              <div className="flex">
                <dt className="text-gray-500 w-32">Father</dt>
                <dd className="text-gray-900 font-medium">{family.father_name || <span className="text-gray-400">—</span>}</dd>
              </div>
              <div className="flex">
                <dt className="text-gray-500 w-32">Mother</dt>
                <dd className="text-gray-900 font-medium">{family.mother_name || <span className="text-gray-400">—</span>}</dd>
              </div>
              <div className="flex">
                <dt className="text-gray-500 w-32">Guardian</dt>
                <dd className="text-gray-900 font-medium">
                  {family.guardian_name ? (
                    <>
                      {family.guardian_name}
                      {family.guardian_relationship && (
                        <span className="text-gray-500 ml-1">({family.guardian_relationship})</span>
                      )}
                    </>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </dd>
              </div>
            </dl>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center">
              <Phone className="h-4 w-4 mr-1.5" />
              Contact
            </h2>
            <dl className="space-y-2 text-sm">
              <div className="flex items-start">
                <Mail className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                <dd className="text-gray-900">
                  {family.contact_email ? (
                    <a href={`mailto:${family.contact_email}`} className="text-blue-600 hover:underline">
                      {family.contact_email}
                    </a>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </dd>
              </div>
              <div className="flex items-start">
                <Phone className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                <dd className="text-gray-900">
                  {family.contact_phone ? (
                    <a href={`tel:${family.contact_phone}`} className="text-blue-600 hover:underline">
                      {family.contact_phone}
                    </a>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </dd>
              </div>
              <div className="flex items-start">
                <MapPin className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                <dd className="text-gray-900 whitespace-pre-line">
                  {family.address || <span className="text-gray-400">—</span>}
                </dd>
              </div>
            </dl>
          </section>
        </div>

        {family.notes && (
          <div className="px-6 pb-5">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2 flex items-center">
              <FileText className="h-4 w-4 mr-1.5" />
              Notes
            </h2>
            <p className="text-sm text-gray-700 whitespace-pre-line bg-gray-50 rounded p-3">
              {family.notes}
            </p>
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Users className="h-5 w-5 mr-2 text-blue-500" />
            Members ({members.length})
          </h2>
        </div>
        {members.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">
            No students linked to this family yet. Use “Edit family” to add members.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Tamil</th>
                  <th className="px-4 py-2 text-left">Date of birth</th>
                  <th className="px-4 py-2 text-left">Age</th>
                  <th className="px-4 py-2 text-left">Gender</th>
                  <th className="px-4 py-2 text-left">Department</th>
                  <th className="px-4 py-2 text-left">House</th>
                  <th className="px-4 py-2 text-left">Reference</th>
                  <th className="px-4 py-2 text-left">Payment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {members.map((m) => (
                  <tr key={m.student_id} className="hover:bg-gray-50 text-sm">
                    <td className="px-4 py-2">
                      <Link to={`/participants/${m.student_id}`} className="text-blue-600 hover:underline font-medium">
                        {m.first_name} {m.last_name}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {m.first_name_tamil || ''} {m.last_name_tamil || ''}
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {format(parseISO(m.date_of_birth), 'dd.MM.yyyy')}
                    </td>
                    <td className="px-4 py-2 text-gray-700">{calculateAge(m.date_of_birth)}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${
                        m.gender === 'M' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
                      }`}>
                        {m.gender === 'M' ? 'Male' : 'Female'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-700">{m.department?.name || '—'}</td>
                    <td className="px-4 py-2">
                      {m.house ? (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs"
                          style={{ backgroundColor: `${m.house.house_color}20`, color: m.house.house_color }}
                        >
                          {m.house.house_name}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-700">{m.reference_number || '—'}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${
                        paymentColor[m.payment_status || 'not_paid'] || 'bg-gray-100 text-gray-700'
                      }`}>
                        {m.payment_status || 'not_paid'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <CreditCard className="h-5 w-5 mr-2 text-blue-500" />
            Payment history
          </h2>
        </div>
        {payments.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No payments recorded.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left">Year</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Amount</th>
                  <th className="px-4 py-2 text-left">Paid at</th>
                  <th className="px-4 py-2 text-left">Method</th>
                  <th className="px-4 py-2 text-left">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[...payments]
                  .sort((a, b) => b.competition_year - a.competition_year)
                  .map((p) => (
                    <tr key={p.competition_year} className="text-sm">
                      <td className="px-4 py-2 font-medium text-gray-900 flex items-center">
                        <Calendar className="h-3.5 w-3.5 mr-1 text-gray-400" />
                        {p.competition_year}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${
                          paymentColor[p.status] || 'bg-gray-100 text-gray-700'
                        }`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        {p.amount != null ? `${p.amount} kr` : '—'}
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        {p.paid_at ? format(parseISO(p.paid_at), 'dd.MM.yyyy') : '—'}
                      </td>
                      <td className="px-4 py-2 text-gray-700">{p.method || '—'}</td>
                      <td className="px-4 py-2 text-gray-700 max-w-xs truncate">{p.notes || '—'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FamilyDetailPage;
